import { createSelector } from 'reselect';
import flow from 'lodash/fp/flow';
import uniqBy from 'lodash/fp/uniqBy';
import map from 'lodash/fp/map';
import filter from 'lodash/fp/filter';

import { Suggestion, SearchResponse, ThinksuggestAd } from '../../types';

import { GlobalState } from '../../../../../types.global';

import {
  createSearchTagSuggestion,
  formatFinalData,
  getFaviconUrl,
  getTotalLimit,
  formatZeropark,
  createOrganicUrl,
} from './utils';
import {
  selectFeatureFlags,
  selectIsUserRegular,
} from '../../../../../store/selectors';
import { selectChoosenSearchTag } from '../searchTagsSelctors';

// --------------

export const selectInstantResultsState = (state: GlobalState): string =>
  state.search.instantResults.state;

export const selectInstantResults = (state: GlobalState): SearchResponse => {
  if (selectInstantResultsState(state) !== 'FULFILLED') return null;
  const { instantResults } = state.search;

  return instantResults.data;
};

const selectEntities = (state: GlobalState) => state.search.$entities;

const selectProviderQuery = (state: GlobalState): string =>
  state.search.provided;

// -----------

// const selectTiles = createSelector(
//   selectShortcuts,
//   selectTopSites,
//   (shortcuts, topSites) => [...shortcuts, ...topSites].map(formatTiles)
// );

const selectOrganicSuggestions = createSelector(
  (state: GlobalState) => state.search.$organic?.list,
  selectChoosenSearchTag,
  (list = [], searchTag) =>
    searchTag
      ? flow(
        filter(({ type }) => type === 'organic'),
        map(createSearchTagSuggestion(searchTag)),
        filter(({ term }) => !!term.trim()),
        uniqBy('url')
      )(list)
      : list
);

const selectInstantResultTerm = (state: GlobalState): string => {
  if (selectInstantResultsState(state) === 'PENDING') {
    return state.search.instantResults.term;
  }

  const { originalQuery } =
    state.search.instantResults.data?.queryContext || {};
  if (!originalQuery) return null;
  return originalQuery.startsWith('site:')
    ? originalQuery.substr(originalQuery.indexOf(' ') + 1)
    : originalQuery;
};

const PLACEHOLDER_SUGGESTIONS = new Array(3).fill('').map(
  (v, i): Suggestion => ({
    type: 'placeholder',
    id: `placeholder-${i}`,
    url: 'https://oceanhero.today/search',
    term: 'loading',
  })
);

const selectInstantResultsSuggestions = createSelector(
  selectInstantResultsState,
  selectInstantResults,
  (instantResultState, instantResults) => {
    if (instantResultState === 'PENDING') {
      return PLACEHOLDER_SUGGESTIONS;
    }

    if (instantResultState !== 'FULFILLED') {
      return null;
    }

    try {
      if (instantResults) {
        return instantResults.webPages.value.map(
          (w): Suggestion => ({
            id: w.id,
            url: w.url,
            displayUrl: (() => {
              try {
                return new URL(w.url).hostname;
              } catch (err) {
                console.error('displayUrl', err);
                return '';
              }
            })(),
            term: w.name,
            label: w.snippet,
            type: 'instantResult',
            favIconUrl: getFaviconUrl(w.url),
          })
        );
      }
    } catch (err) {
      console.error('selectSuggestions', err);
      return null;
    }
  }
);

const preSelectSuggestions = (state): Suggestion[] => {
  const query = selectProviderQuery(state);
  const featureFlags = selectFeatureFlags(state);
  const entities = selectEntities(state);
  const organic = selectOrganicSuggestions(state);
  const isActiveSearchTag = !!state.search.searchTags.activeId;

  const { ZP_SUGGESTIONS } = featureFlags;

  if (!query && isActiveSearchTag) return [];

  const isRegular = selectIsUserRegular(state);
  const isThinksuggestAds = organic.find(
    ({ type, provider }) => (type === 'organic' && provider) || type === 'ad'
  );

  const isZeroparkOk = ZP_SUGGESTIONS && isRegular;

  const data = [
    ...(isZeroparkOk && !isThinksuggestAds
      ? formatZeropark(query)(entities.zp)
      : []),

    ...(query && organic ? organic : []),

    // ...(isActiveSearchTag || isThinksuggestAds) ? [] :
    //   formatHistory(query, engine, !MAGIC_SEARCH_BOX)(entities.history),

    // ...((
    //   !isActiveSearchTag &&
    //   query.length >= 2 &&
    //   MAGIC_SEARCH_BOX &&
    //   !isThinksuggestAds
    // ) ? [
    // ...preparePriorityOrder(query)(tiles) as Suggestion[],
    // ...preparePriorityOrder(query)(entities.tabs.map(formatTabs) as Suggestion[]),
    // ...formatPopularWebsites(query)(entities.popularWebsites),
    // ] : []),
  ] as Suggestion[];

  return formatFinalData(query, featureFlags, data);
};

const selectMemoizedPreSuggestions = (() => {
  const inMemory = {
    keys: [],
    data: undefined,
  };

  return (state: GlobalState): Suggestion[] => {
    const key1 = (() => {
      const { provided } = state.search;
      const { term } = state.search.$organic || {};
      if (provided) return term;
      const searchTag = selectChoosenSearchTag(state);
      return searchTag ? searchTag.u : provided;
    })();

    const $history = selectEntities(state).history;

    const newHistory = state.search.history;
    const provided = state.search.provided;

    if (
      !inMemory.data ||
      key1 !== inMemory.keys[0] ||
      $history !== inMemory.keys[1]
    ) {
      inMemory.keys[0] = key1;
      inMemory.keys[1] = $history;
      inMemory.data = preSelectSuggestions(state);
    }

    const data = inMemory.data;

    const parseHistory = (term) => {
      return {
        url: createOrganicUrl({ term: term }, state.search.engine || 'oh'),
        id: createOrganicUrl({ term: term }, state.search.engine || 'oh'),
        type: 'history' as Suggestion['type'],
        term: term,
      };
    };

    const newData = [
      data[0],
      ...newHistory
        .filter(
          (item) =>
            item.toLocaleLowerCase().startsWith(provided.toLocaleLowerCase()) &&
            item !== provided
        )
        .map(parseHistory)
        .slice(0, 4),
      ...data.slice(1),
    ]
      .filter(Boolean)
      .slice(0, 10);

    // console.log('newData', newData);

    return newData;
  };
})();

const getAdsList = (value) =>
  Array.isArray(value) ? value : Object.values(value);

export const selectInstantAds = createSelector(
  (state: GlobalState) => state.search.$thinksuggestData,
  ({ suggestions, term, provided }) => {
    if (!suggestions) return null;

    const formatValue = (value) => value.replace('~', term);

    return getAdsList(suggestions['~'] || []).map(
      (v: ThinksuggestAd): Suggestion => ({
        id: v.h,
        displayUrl: formatValue(v.u),
        term: formatValue(v.l || v.d),
        type: 'instantResultAd',
        favIconUrl: getFaviconUrl(formatValue(v.u)),
        url: (() => {
          const query = new URLSearchParams({
            m: 'c',
            subid: 'ntp',
            pid: process.env.THINKSUGGEST_PID,
            q: provided,
            k: term,
            t: v.t,
            h: v.h,
            s: v.s,
          });
          return `https://api.thinksuggest.org?${query}`;
        })(),
      })
    );
  }
);

export const selectSuggestions = createSelector(
  selectMemoizedPreSuggestions,
  selectProviderQuery,

  selectInstantResultsSuggestions,
  selectInstantResultTerm,
  selectInstantAds,
  (data, provided, instant, instantTerm, instantAds): Suggestion[] => {
    if (!provided || !instant) return data;

    const limit = getTotalLimit();

    const foundIndex = data.findIndex(
      (s) =>
        ['organicProvided', 'organic', 'searchTag', 'historyOrganic'].includes(
          s.type
        ) && s.term === instantTerm
    );

    if (foundIndex === -1) return data;

    return [
      ...data.slice(0, foundIndex + 1),
      ...[
        // try to have an ad
        instantAds?.[0],
        ...instant,
      ]
        .filter(Boolean)
        .slice(0, instantAds?.[0] ? 4 : 3),
      ...data.slice(foundIndex + 1),
    ]
      .filter(({ url }) => !!url)
      .slice(0, limit);
  }
);

const findSuggestion = (state: GlobalState, searchId): Suggestion =>
  searchId ? selectSuggestions(state).find(({ id }) => id === searchId) : null;

export const selectHighlighted = (state: GlobalState): Suggestion =>
  findSuggestion(state, state.search.highlighted.id);

export const selectInstantResultsHighlighted = (
  state: GlobalState
): Suggestion => {
  if (!state.search.isFocused) return null;

  const suggestions = selectSuggestions(state);
  const instantResults = suggestions.filter(
    ({ type }) => type === 'instantResult'
  );

  if (!instantResults.length) return null;

  return [
    findSuggestion(state, state.search.highlighted.mouse),
    selectHighlighted(state),
  ]
    .filter(Boolean)
    .find((highlighted) => highlighted.type === 'instantResult');

  // if (highlightedSuggestion) {
  //   return highlightedSuggestion;
  // }

  // const data = selectInstantResults(state);
  // return data.webPages?.value?.find((page) => page.url === url);
};
